import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StaffNavbar from '../components/StaffNavbar';
import BackButton from '../components/BackButton';
import SchoolPicker from '../components/SchoolPicker';
import apiService from '../services/api';
import '../styles/StaffPages.css';

const groupOrder = ['linked_card', 'safari_extension', 'first_purchase', 'all_three_completed'];

const sectionStyle = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
};

const cardStyle = {
  background: '#f8f9fa',
  borderRadius: 12,
  padding: '16px 18px',
};

const formatDateTime = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' });
};

const formatDate = value => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const stepTimestampByGroup = {
  linked_card: user => user.linked_card_at,
  safari_extension: user => user.safari_extension_at,
  first_purchase: user => user.first_purchase_at,
};

const formatUserSubtitle = user => {
  const pieces = [];
  if (user.id) pieces.push(`ID: ${user.id}`);
  if (user.fan_id) pieces.push(`Fan ID: ${user.fan_id}`);
  if (typeof user.current_points === 'number') pieces.push(`Points: ${user.current_points}`);
  return pieces.join(' · ');
};

const formatAllThreeTimestamps = user => (
  <>
    <div>Card linked: {formatDateTime(user.linked_card_at)}</div>
    <div>Safari extension: {formatDateTime(user.safari_extension_at)}</div>
    <div>First purchase: {formatDateTime(user.first_purchase_at)}</div>
  </>
);

const UserRow = ({ user, groupKey }) => {
  const stepAt = stepTimestampByGroup[groupKey]?.(user);
  const stepLabel = {
    linked_card: 'Card linked',
    safari_extension: 'Safari extension activated',
    first_purchase: 'First purchase',
  }[groupKey];

  return (
    <div
      style={{
        padding: '14px 0',
        borderBottom: '1px solid #eceff3',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontWeight: 700, color: '#111827' }}>
            {user.full_name} {' — '} {user.email}
          </div>
          <div className="staff-page-subheader" style={{ marginTop: 4, opacity: 0.8 }}>
            {formatUserSubtitle(user) || '—'}
          </div>
        </div>
        <div
          className="staff-page-subheader"
          style={{ textAlign: 'right', minWidth: 220, opacity: 0.8 }}
        >
          {groupKey === 'all_three_completed' ? (
            formatAllThreeTimestamps(user)
          ) : (
            <div>
              {stepLabel}: {formatDateTime(stepAt)}
            </div>
          )}
          <div>Account created: {formatDate(user.created_at)}</div>
          <div>
            Active school: {user.active_school_name || '—'}
            {user.is_primary_school ? ' · Primary' : ''}
          </div>
        </div>
      </div>
    </div>
  );
};

const UsersGroupSection = ({ title, count, users, groupKey }) => (
  <div style={sectionStyle}>
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 12,
        marginBottom: 16,
      }}
    >
      <h2 className="staff-page-header" style={{ fontSize: 22, margin: 0 }}>
        {title} ({count})
      </h2>
    </div>
    {users.length ? (
      <div>
        {users.map(user => (
          <UserRow key={user.id} user={user} groupKey={groupKey} />
        ))}
      </div>
    ) : (
      <p className="staff-page-content" style={{ margin: 0 }}>
        No users in this group.
      </p>
    )}
  </div>
);

const SuperuserUsersProgress = () => {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [schools, setSchools] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(true);
  const [selectedSchoolId, setSelectedSchoolId] = useState('');
  const [progressData, setProgressData] = useState(null);
  const [progressLoading, setProgressLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const currentUser = await apiService.fetchCurrentUser();
        if (!currentUser?.is_superuser) {
          navigate('/staff/dashboard/');
          return;
        }
        setAuthChecked(true);
      } catch (err) {
        console.error('Failed to fetch user:', err);
        navigate('/login/');
      }
    };

    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const loadSchools = async () => {
      if (!authChecked) return;

      setSchoolsLoading(true);
      try {
        const data = await apiService.getSchools();
        const list = Array.isArray(data) ? data : data?.results || [];
        const sorted = [...list].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setSchools(sorted);
      } catch (err) {
        console.error('Failed to load schools:', err);
        setSchools([]);
        setError('Failed to load schools.');
      } finally {
        setSchoolsLoading(false);
      }
    };

    loadSchools();
  }, [authChecked]);

  // The picker works in school objects while everything below keys off the id,
  // so derive one from the other rather than duplicating state.
  const selectedSchool =
    schools.find(school => String(school.id) === String(selectedSchoolId)) || null;

  useEffect(() => {
    const loadProgress = async () => {
      if (!authChecked || !selectedSchoolId) return;

      setProgressLoading(true);
      setError('');
      try {
        const data = await apiService.getSuperuserUsersProgress({ school_id: selectedSchoolId });
        setProgressData(data);
      } catch (err) {
        console.error('Failed to load users progress:', err);
        setProgressData(null);
        setError('Failed to load users progress.');
      } finally {
        setProgressLoading(false);
      }
    };

    loadProgress();
  }, [authChecked, selectedSchoolId]);

  const summaryCards = useMemo(() => {
    if (!progressData?.groups) return [];
    return groupOrder.map(key => ({
      key,
      title: progressData.groups[key]?.title || key,
      count: progressData.groups[key]?.count || 0,
    }));
  }, [progressData]);

  if (!authChecked || schoolsLoading) {
    return (
      <div className="admin-dashboard">
        <StaffNavbar activeSection="superuser" />
        <div className="admin-main">
          <div className="admin-content">
            <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
            <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <StaffNavbar activeSection="superuser" />
      <div className="admin-main">
        <div className="admin-content">
          <BackButton text="Back to Superuser Dashboard" to="/superuser/dashboard/" />
          <h1 className="staff-page-header">Account Setup Progress</h1>
          <p className="staff-page-subheader">
            First 3-step onboarding progress by school.
          </p>

          <div style={{ ...sectionStyle, marginTop: 24, marginBottom: 24 }}>
            {schools.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                No schools found.
              </div>
            ) : (
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: 8,
                    fontFamily: '"Baikal Book"',
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#374151',
                  }}
                >
                  Select School
                </label>
                <SchoolPicker
                  schools={schools}
                  value={selectedSchool}
                  onChange={school => setSelectedSchoolId(school?.id || '')}
                />
                {progressData?.school ? (
                  <p className="staff-page-subheader" style={{ marginTop: 12, marginBottom: 0 }}>
                    {progressData.school.name} · {progressData.total_users} total users
                  </p>
                ) : null}
              </div>
            )}
          </div>

          {error ? (
            <div style={{ ...sectionStyle, marginBottom: 24 }}>
              <p className="staff-page-content" style={{ color: '#c62828', margin: 0 }}>
                {error}
              </p>
            </div>
          ) : null}

          {!selectedSchoolId ? (
            <div style={{ ...sectionStyle, opacity: 0.7 }}>
              <p className="staff-page-content" style={{ margin: 0 }}>
                Please select a school
              </p>
            </div>
          ) : progressLoading ? (
            <div style={sectionStyle}>
              <p className="staff-page-content" style={{ margin: 0 }}>
                Loading users progress...
              </p>
            </div>
          ) : progressData ? (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                  gap: 16,
                  marginBottom: 24,
                }}
              >
                <div style={cardStyle}>
                  <div className="staff-page-subheader" style={{ opacity: 0.75 }}>
                    Total users
                  </div>
                  <div style={{ fontSize: 28, fontWeight: 800 }}>{progressData.total_users}</div>
                </div>
                {summaryCards.map(card => (
                  <div key={card.key} style={cardStyle}>
                    <div className="staff-page-subheader" style={{ opacity: 0.75 }}>
                      {card.title}
                    </div>
                    <div style={{ fontSize: 28, fontWeight: 800 }}>{card.count}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'grid', gap: 24, marginBottom: 48 }}>
                {groupOrder.map(key => (
                  <UsersGroupSection
                    key={key}
                    groupKey={key}
                    title={progressData.groups[key]?.title || key}
                    count={progressData.groups[key]?.count || 0}
                    users={progressData.groups[key]?.results || []}
                  />
                ))}
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default SuperuserUsersProgress;
